import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setLoading } from 'store/reducers/mainSlice';
import { LuChevronDown } from 'react-icons/lu';
import { Dropdown } from 'react-bootstrap';
import NoRecordFoundIcon from 'assets/images/icons/no-records-found.svg';
import {
	ClosedBoxIcon,
	CheckedBoxIcon,
} from 'ui-component/custom-icons/icons';

import { FaPlus, FaSearch, FaFilter } from 'react-icons/fa';
import Filter from 'ui-component/filter-menu/Filter';
import { Skeleton } from '@mui/material';
import PermissionAllowed from 'ui-component/permissions/PermissionAllowed';
import usePermissions from 'hooks/usePermissions';
import usePageTitle from 'hooks/usePageTitle';
import { BsFiletypeXls } from 'react-icons/bs';
import DeleteConfirmation from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmation';
import toast from 'react-hot-toast';

import NoImage from 'assets/images/no-image.png';
import useIsMobile from 'utils/isMobileUtils';



// ✅ Clear
const Index = () => {
	const [setPageTitle] = usePageTitle('Products');
	useEffect(() => {
		return () => {
			setPageTitle(null);
		};
	}, []);
	const history = useNavigate();
	const [productList, setProductList] = useState([]);
	const [show, setShow] = useState('');
	const [allPermissions, hasEveryPermission] = usePermissions();
	const dispatch = useDispatch();
	const [isProductPageInactive, setIsProductPageInactive] = useState(false);

	const [searchField, setSearchField] = useState();
	const [searchValue, setSearchValue] = useState();
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [filters, setFilters] = useState([]);
	const [activeFilters, setActiveFilters] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [initializedProductList, setInitializedProductList] = useState(false);
	const [indexToDelete, setIndexToDelete] = useState(null);
	const [showInStocksOnly, setShowInStocksOnly] = useState(true);


    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableRef = useRef();
	const [showPreloadingRows, setShowPreloadingRows] = useState(true);
	const [totalRecords, setTotalRecords] = useState(0);
	const isMobile = useIsMobile();
	

	const location = useLocation();
	const pathParts = location.pathname;


	useEffect(() => {
		const handleScroll = () => {
			if (!tableRef.current || showPreloadingRows || !hasMore) return;
			const { scrollTop, scrollHeight, clientHeight } = tableRef.current;
			// Trigger only when user reaches the bottom (with a small threshold)
			if (scrollTop + clientHeight >= scrollHeight - 10) {
				loadMoreProducts();
			}
		};
		const currentRef = tableRef.current;
		if (currentRef) {
			currentRef.addEventListener('scroll', handleScroll);
		}
		return () => {
			if (currentRef) {
				currentRef.removeEventListener('scroll', handleScroll);
			}
		};
	}, [showPreloadingRows, hasMore, productList]);



	const loadMoreProducts = () => {
		if (showPreloadingRows || !hasMore) return;
		// setIsLoading(true);
		setShowPreloadingRows(true);
		let request = {};
		request.page = page + 1;
		request.status = !isProductPageInactive ? 'active' : 'inactive';
		request.search = searchField?.length > 2 ? searchField : null;
		request.showInStocksOnly = showInStocksOnly;
		api.getProductList(request, activeFilters).then((res) => {
			// Use pagination meta from backend
			const nextPage = res.data.meta?.current_page || request.page;
			const lastPage = res.data.meta?.last_page || nextPage;
			if (nextPage > lastPage || res.data.data.length === 0) {
				setHasMore(false);
			} else {
				setProductList(prev => [...prev, ...res.data.data]);
				setPage(nextPage);
			}
			// setIsLoading(false);
			setShowPreloadingRows(false);
		});
	};
	
	const initializeData = () => {
        // Initial load
		let request = {}
		request.page = 1;
		request.status = !isProductPageInactive ? 'active' : 'inactive';
		request.search = searchField?.length > 2 ? searchField : null;
		request.showInStocksOnly = showInStocksOnly;
        api.getProductList(request, activeFilters).then((res) => {
            setProductList(res.data.data);
            setHasMore(res.data.data.length > 0);
            setPage(1);
            dispatch(setLoading(false));
            setIsLoading(false);
			setShowPreloadingRows(false);
			setTotalRecords(res?.data?.total || 0);
        });

	}
	useEffect(() => {
        setIsLoading(true);
		setShowPreloadingRows(true);
		
		setProductList([]);
		initializeData();
    }, [isProductPageInactive, searchField, activeFilters, showInStocksOnly]);


	useEffect(() => {
		if (initializedProductList) {
			if (pathParts == '/products') {
				setIsProductPageInactive(false);
			} else {
				setIsProductPageInactive(true);
			}
		} else {
			setInitializedProductList(true);
			if (pathParts == '/products') {
				setIsProductPageInactive(false);
			} else {
				setIsProductPageInactive(true);
			}
		}
		setSearchField('');

		getFiltersList();
	}, [location]); //location

	const viewData = (id) => {
		history(`/products/${id}`);
	};

	const deleteProductData = (data) => {
		setShow(data);
	};
	const handleClose = () => setShow('');

	const handleDelete = (id) => {
		api.deleteProductDetail(id).then((res) => {
			if (res.status) {
				setShow('');
				const updatedProductList = productList.filter((_, index) => index !== indexToDelete);
				setProductList(updatedProductList);
				setTotalRecords(prev => prev - 1);
				toast.success(res.message);
			} else {
				setShow('');
				toast.error(res.message);
			}
		});
	};

	const ImportProduct = () => {
		history('/products/import');
	};
	const ExportProduct = () => {
		history('/products/export');
	};

	const getFiltersList = () => {
		api.getProductFilters().then((res) => {
			setFilters(res.data);
		});
	};

	const handleSearch = (e) => {
		let text = e.target.value;
		setSearchValue(text);


		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// Only update searchField after user stops typing for 500ms
		let timeout = setTimeout(() => {
			setSearchField(text);
		}, 500);

		setSearchTimeout(timeout);
	};

	return (
		<>
			<div className="row">
				<div className="col-xl-12 col-lg-12">
					<div className="show-on-mobile new-page-header" style={{ position: 'relative' }}>
						<div className="header-bubble" />
						<div className="d-flex flex-column gap-2 " style={{ position: 'relative', zIndex: 1, paddingTop: 10, width: '100%' }}>
							<h5 style={{ textAlign: 'center' }}>Products</h5>
							<div className="horizontal-scroll" style={{ paddingLeft: 20, paddingRight: 20, display: 'flex', gap: 8 }}>
								<PermissionAllowed permissions={['Create Model (Button)']}>
									<div className="actions" style={{ display: 'inline-block' }}>
										<Link
											to={'/products/create'}
											className="btn btn-primary btn-outlined-main white-var"
											style={{ minWidth: 120, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}
										>
											<FaPlus style={{ marginRight: 6 }} />
											New Product
										</Link>
									</div>
								</PermissionAllowed>
								<div className="actions" style={{ display: 'inline-block' }}>
									{isProductPageInactive ? (
										<Link
											to={'/products'}
											className="btn btn-primary btn-outlined-main white-var"
											style={{ minWidth: 120, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}
										>
											<FaFilter style={{ marginRight: 6 }} />
											Active Products
										</Link>
									) : (
										<Link
											to={'/products/inactive'}
											className="btn btn-primary btn-outlined-main white-var"
											style={{ minWidth: 120, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}
										>
											<FaFilter style={{ marginRight: 6 }} />
											Inactive Products
										</Link>
									)}
								</div>
								<PermissionAllowed permissions={['Create Model (Button)']}>
									<div className="actions" style={{ display: 'inline-block' }}>
										<Dropdown className="btnDel iconbtn product-import-export-dropdown" align={'end'}>
											<Dropdown.Toggle as="span" style={{ padding: 0 }}>
												<button
													type="button"
													className="btn btn-primary btn-outlined-main white-var"
													style={{
														minWidth: 120,
														height: 38,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														fontSize: 15
													}}
												>
													<BsFiletypeXls style={{ fontSize: '1.4em', marginRight: 6 }} />
													Import/Export <LuChevronDown style={{ marginLeft: 6 }} />
												</button>
											</Dropdown.Toggle>
											<Dropdown.Menu>
												<Dropdown.Item onClick={() => ImportProduct(1)}>Import products</Dropdown.Item>
												<Dropdown.Item onClick={() => ExportProduct()}>Export products</Dropdown.Item>
											</Dropdown.Menu>
										</Dropdown>
									</div>
								</PermissionAllowed>
							</div>
						</div>
					</div>
					<div className="section-head-new mb-1 mar102000 hide-on-mobile">
						<h2>
							<PermissionAllowed permissions={['Create Model (Button)']}>
								<div className="actions mb10">
									<Link to={'/products/create'} className="btn btn-primary btn-outlined-main-primary white-var">
										<FaPlus /> New Product
									</Link>
								</div>
							</PermissionAllowed>
						</h2>
						<div className="actions" style={{ marginBottom: '10px' }}>
							{isProductPageInactive ? (
								<Link to={'/products'} className="btn btn-primary btn-outlined-main white-var">
									<FaFilter /> Active Products
								</Link>
							) : (
								<Link to={'/products/inactive'} className="btn btn-primary btn-outlined-main white-var">
									<FaFilter /> Inactive Products
								</Link>
							)}
							<PermissionAllowed permissions={['Create Model (Button)']}>
								<Dropdown className="btnDel iconbtn product-import-export-dropdown" align={'end'}>
									<Dropdown.Toggle as="span">
										<Link to={''} className="btn btn-primary btn-outlined-main white-var btn-create-report" style={{ minWidth: 'unset' }}>
											<BsFiletypeXls style={{ fontSize: '1.4em' }} /> Import/Export <LuChevronDown />
										</Link>
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Dropdown.Item onClick={() => ImportProduct(1)}>Import products</Dropdown.Item>
										<Dropdown.Item onClick={() => ExportProduct()}>Export products</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
							</PermissionAllowed>
							<button
								className="btn btn-primary btn-outlined-main-primary white-var"
								onClick={() => {
									setShowInStocksOnly(!showInStocksOnly);
								}}
							>
								{
									!showInStocksOnly ? (
										<div className="flex justify-center align-center gap-2">
											<div>
												<ClosedBoxIcon width={'18px'} height={'18px'} />
											</div>
											<div>
												All Products
											</div>
										</div>
									) : (
										<div className="flex justify-center align-center gap-2">
											<div>
												<CheckedBoxIcon width={'18px'} height={'18px'} />
											</div>
											<div>
												In-stock Products
											</div>
										</div>
									)
								}
							</button>
						</div>
					</div>
					<div
						className="show-on-mobile"
						style={{
							display: 'flex',
							paddingLeft: 20,
							paddingRight: 20,
							paddingTop: 0,
							paddingBottom: 0,
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 10
						}}
					>
						<form className="form-inline navbar-search" style={{ flex: 1 }}>
							<div style={{ position: 'relative', width: '100%' }}>
								<input
									type="text"
									className="form-control bg-white border"
									placeholder="Search"
									aria-label="Search"
									aria-describedby="basic-addon2"
									onChange={handleSearch}
									value={searchValue}
									style={{ width: '100%', paddingRight: 36 }}
								/>
								<span
									className="searchIcon"
									style={{
										position: 'absolute',
										right: 10,
										top: '50%',
										transform: 'translateY(-50%)',
										color: '#888',
										pointerEvents: 'none'
									}}
								>
									<FaSearch />
								</span>
							</div>
						</form>
						<div style={{ flexShrink: 0 }}>
							<Filter activeFilters={activeFilters} onSubmit={setActiveFilters} filters={filters} />
						</div>
					</div>

					<div className="card shadow-sm bg-white mb-4 table-card">
						<div className=" align-items-center justify-content-between TableSerachBar">
							<div>
								<Filter activeFilters={activeFilters} onSubmit={setActiveFilters} filters={filters} />
							</div>
							<form className="form-inline navbar-search">
								<div className="input-group">
									<input
										type="text"
										className="form-control bg-white border"
										placeholder="Search"
										aria-label="Search"
										aria-describedby="basic-addon2"
										onChange={handleSearch}
										value={searchValue}
										style={{ width: '100%' }}
									/>
									<span className="searchIcon">
										<FaSearch />
									</span>
								</div>
							</form>
						</div>
						<div className="estimation-table-area">
							<div className="table-side">
								{!isMobile ? (
									<div
										className="table-responsive-xl tableData estimationList"
										ref={tableRef}
										style={{ height: 'calc(100vh - 230px)', overflowY: 'auto' }}
									>
										<table className="table pm-table">
											<thead>
												<tr className="sticky-header ">
													<th scope="col" className="" style={{ width: '50px' }}></th>
													<th scope="col" className="" style={{ width: '150px' }}>
														SKU
													</th>
													<th scope="col" className="">
														Product
													</th>
													<th scope="col" className="align-left" style={{ width: '130px' }}>
														Price
													</th>
													<th scope="col" style={{ width: '130px' }}>
														Stock
													</th>
													<PermissionAllowed permissions={['Delete Product (Button)']}>
														<th scope="col" className="text-center" style={{ width: '50px' }}>
															Action
														</th>
													</PermissionAllowed>
												</tr>
											</thead>
											<tbody>
												{productList?.length > 0 && (
													productList?.map((row, index) => {
														return (
															<tr key={index}>
																<td
																	className="align-middle"
																	onClick={() => {
																		if (hasEveryPermission(['View Product (Button)'])) {
																			viewData(row.id);
																		}
																	}}
																	style={{
																		cursor: hasEveryPermission(['View Product (Button)']) ? 'pointer' : 'not-allowed',
																		height: '65px',
																	}}
																>
																	{
																		(hasEveryPermission(['View Product (Button)'])) ? (
																			<Link to={`/products/${row.id}`} className="text-black">
																				{row?.thumbnail_url ? (
																					<img src={row?.thumbnail_url} style={{ width: '30px', height: '50px' }} />
																				) : row?.custom_thumbnail_url ? (
																					<img src={row?.custom_thumbnail_url} style={{ width: '30px', height: '50px' }} />
																				) : (
																					<img src={NoImage} style={{ width: '30px' }}  />
																				)}
																			</Link>
																		) : (
																			row?.thumbnail_url ? (
																				<img src={row?.thumbnail_url} style={{ width: '30px', height: '50px' }} />
																			) : row?.custom_thumbnail_url ? (
																				<img src={row?.custom_thumbnail_url} style={{ width: '30px', height: '50px' }} />
																			) : (
																				<img src={NoImage} style={{ width: '30px' }}  />
																			)
																		)
																	}
																</td>
																<td
																	className="align-middle "
																	onClick={() => {
																		if (hasEveryPermission(['View Product (Button)'])) {
																			viewData(row.id);
																		}
																	}}
																	style={{
																		cursor: hasEveryPermission(['View Product (Button)']) ? 'pointer' : 'not-allowed',
																		height: '65px',
																	}}
																>
																	{
																		(hasEveryPermission(['View Product (Button)'])) ? (
																			<Link to={`/products/${row.id}`} className="text-black">
																				{row?.sku}
																			</Link>
																		) : (
																			row?.sku
																		)
																	}
																</td>
																<td
																	className="align-middle "
																	onClick={() => {
																		if (hasEveryPermission(['View Product (Button)'])) {
																			viewData(row.id);
																		}
																	}}
																	style={{
																		cursor: hasEveryPermission(['View Product (Button)']) ? 'pointer' : 'not-allowed',
																		height: '65px',
																	}}
																>
																	{
																		(hasEveryPermission(['View Product (Button)'])) ? (
																			<Link to={`/products/${row.id}`} className="text-black">
																				{row?.name}
																			</Link>
																		) : (
																			row?.name
																		)
																	}
																</td>
																<td
																	className="align-left "
																	onClick={() => {
																		if (hasEveryPermission(['View Product (Button)'])) {
																			viewData(row.id);
																		}
																	}}
																	style={{
																		cursor: hasEveryPermission(['View Product (Button)']) ? 'pointer' : 'not-allowed',
																		height: '65px',
																	}}
																>
																	{
																		(hasEveryPermission(['View Product (Button)'])) ? (
																			<Link to={`/products/${row.id}`} className="text-black">
																				$ {row?.price ?? '0.00'}
																			</Link>
																		) : (
																			<>
																				$ {row?.price ?? '0.00'}
																			</>
																		)
																	}
																</td>
																<td
																	onClick={() => {
																		if (hasEveryPermission(['View Product (Button)'])) {
																			viewData(row.id);
																		}
																	}}
																	style={{
																		cursor: hasEveryPermission(['View Product (Button)']) ? 'pointer' : 'not-allowed',
																		height: '65px',
																	}}
																>
																	{
																		(hasEveryPermission(['View Product (Button)'])) ? (
																			<Link to={`/products/${row.id}`} className="text-black">
																				
																				<div>{row?.stocks?.total}</div>
																				<div className="textGray">Free: {row?.stocks?.free}</div>
																			</Link>
																		) : (
																			<>
																				
																				<div>{row?.stocks?.total}</div>
																				<div className="textGray">Free: {row?.stocks?.free}</div>
																			</>
																		)
																	}
																</td>

																<td
																	className="align-middle text-center action-col"
																	style={{
																		height: '65px',
																	}}
																>
																	<PermissionAllowed permissions={['Delete Product (Button)']} hasSome>
																		<a
																			href="javascript:void(0);"
																			onClick={() => {
																				if (hasEveryPermission(['Delete Returns (Button)'])) {
																					deleteProductData(row);
																					setIndexToDelete(index);
																				}
																			}}
																			style={{ marginLeft: '3px' }}
																		>
																			<svg
																				id="vuesax_linear_trush-square"
																				data-name="vuesax/linear/trush-square"
																				xmlns="http://www.w3.org/2000/svg"
																				width="28"
																				height="20"
																				viewBox="0 0 31.855 31.855"
																			>
																				<g id="trush-square">
																					<path
																						id="Vector"
																						d="M0,17.255V9.291C0,2.655,2.655,0,9.291,0h7.964c6.637,0,9.291,2.655,9.291,9.291v7.964c0,6.637-2.655,9.291-9.291,9.291H9.291C2.655,26.546,0,23.891,0,17.255Z"
																						transform="translate(2.655 2.655)"
																						fill="none"
																						stroke="#ff342d"
																						strokeLinecap="round"
																						strokeLinejoin="round"
																						strokeWidth="2"
																					/>
																					<g id="Group" transform="translate(8.097 8.548)">
																						<path
																							id="Vector-2"
																							data-name="Vector"
																							d="M0,.438,1.779.265A51.533,51.533,0,0,1,6.942,0a86.992,86.992,0,0,1,8.72.438"
																							transform="translate(0 3.026)"
																							fill="none"
																							stroke="#ff342d"
																							strokeLinecap="round"
																							strokeLinejoin="round"
																							strokeWidth="2"
																						/>
																						<path
																							id="Vector-3"
																							data-name="Vector"
																							d="M0,2.588.186,1.447C.319.624.425,0,1.9,0H4.181C5.654,0,5.76.65,5.893,1.447l.186,1.128"
																							transform="translate(4.792)"
																							fill="none"
																							stroke="#ff342d"
																							strokeLinecap="round"
																							strokeLinejoin="round"
																							strokeWidth="2"
																						/>
																						<path
																							id="Vector-4"
																							data-name="Vector"
																							d="M11.932,0l-.571,8.76c-.093,1.367-.173,2.429-2.6,2.429H3.172c-2.429,0-2.509-1.062-2.6-2.429L0,0"
																							transform="translate(1.858 3.57)"
																							fill="none"
																							stroke="#ff342d"
																							strokeLinecap="round"
																							strokeLinejoin="round"
																							strokeWidth="2"
																						/>
																					</g>
																					<path id="Vector-5" data-name="Vector" d="M0,0H31.855V31.855H0Z" fill="none" opacity="0" />
																				</g>
																			</svg>
																		</a>
																	</PermissionAllowed>
																</td>
															</tr>
														);
													})
												)}
												{showPreloadingRows && (
													[...Array(100)].map((e, i) => (
														// <tr>
														// 	{[...Array(6)].map((e, i) => (
														// 		<td>
														// 			<Skeleton variant="rounded" width={'100%'} height={43}></Skeleton>
														// 		</td>
														// 	))}
														// </tr>

														<tr style={{ height: '65px' }} key={i}>
															{/* {[...Array(6)].map((e, i) => ( */}
																<td><Skeleton variant="rounded" width={'100%'} height={43} style={{ borderRadius: '12px' }} /></td>
																<td><Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} /></td>
																<td><Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} /></td>
																<td><Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} /></td>
																<td><Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} /></td>
																<td><Skeleton variant="rounded" width={'100%'} height={15} style={{ borderRadius: '32px' }} /></td>
															{/* ))} */}
														</tr>
													))
												)}

												{!showPreloadingRows && productList?.length < 1 && (
													<tr className="no-hover">
														<td colSpan={6}>
															<div
																className="not-found-container"
																style={{
																	height: 'calc(100vh - 288px)'
																}}
															>
																<div className="not-found-icon">
																	<img src={NoRecordFoundIcon} style={{ height: '200px', width: '200px' }} />
																</div>
																<h6 className="not-found-text">No Data Available</h6>

																<PermissionAllowed permissions={['Create New Product (Action)']}>
																	<Link to={'/purchasing/create'} className="btn btn-primary btn-outlined-main">
																		Create Product
																	</Link>
																</PermissionAllowed>
															</div>
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								) : (
									<div
										className="mobile-product-list"
										ref={tableRef}
										style={{
											height: 'calc(100dvh - 300px)',
											overflowY: 'auto',
											display: 'flex',
											flexDirection: 'column'
										}}
									>
										{isLoading ? (
											[...Array(10)].map((_, i) => (
												<div
													key={i}
													className="mobile-product-card skeleton"
													style={{ borderRadius: 12, background: '#f5f5f5', padding: 16, minHeight: 80 }}
												>
													<Skeleton variant="rounded" width={'100%'} height={60} />
												</div>
											))
										) : productList?.length > 0 ? (
											productList.map((row, index) => (
												<div
													key={index}
													className="mobile-product-card"
													style={{
														borderBottom: '1px solid #eee',
														padding: '10px 20px',
														background: index % 2 === 0 ? '#fff' : '#f9f9f9',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: 12,
														width: '100%'
													}}
													onClick={() => {
														if (hasEveryPermission(['View Product (Button)'])) {
															viewData(row.id);
														}
													}}
												>
													<div style={{ display: 'flex', flexDirection: 'column' }}>
														<div style={{ fontWeight: 600, fontSize: 13 }}>{row?.name}</div>
														<div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
															<div style={{ color: '#888', fontSize: 13 }}>SKU: {row?.sku}</div>
															<div>|</div>
															<div style={{ color: '#888', fontSize: 13 }}>Stock: {row?.stocks?.total}</div>
														</div>{' '}
														<div style={{ textAlign: 'left', fontWeight: 500, fontSize: 13 }}>${row?.price}</div>
													</div>
													<div>
														<img
															src={row?.thumbnail_url || row?.custom_thumbnail_url || NoImage}
															alt="product"
															style={{ width: 'auto', height: 60, objectFit: 'cover', borderRadius: 8 }}
														/>
													</div>
												</div>
											))
										) : (
											<div className="not-found-container" style={{ textAlign: 'center', padding: 40 }}>
												<div className="not-found-icon">
													<img src={NoRecordFoundIcon} style={{ height: '120px', width: '120px' }} />
												</div>
												<h6 className="not-found-text">No Data Available</h6>
												<PermissionAllowed permissions={['Create New Product (Action)']}>
													<Link to={'/purchasing/create'} className="btn btn-primary btn-outlined-main">
														Create Product
													</Link>
												</PermissionAllowed>
											</div>
										)}
									</div>
								)}
								<div
									className="hide-on-mobile"
									style={{
										padding: '10px 0 0 20px',
										borderTop: '1px solid #e0e0e0e0',
										display: 'flex',
										gap: '5px'
									}}
								>
									<div className="text-gray-800">Total Record:</div>
									<div className="fw-500">
										{isLoading ? (
											<div style={{ width: '100px' }}>
												<Skeleton variant="rounded" width={'100%'} height={20}></Skeleton>
											</div>
										) : (
											totalRecords.toLocaleString('en-US')
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{show && (
				<DeleteConfirmation
					show={show}
					handleClose={handleClose}
					handleDelete={handleDelete}
					title="Confirm"
					message={`Are you sure you want to delete ${show.name}?`}
				/>
			)}
		</>
	);
};


export default Index;

